import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { Book3D } from "./Book3D";
import { Magnetic } from "./Magnetic";
import { CheckoutButton } from "./CheckoutButton";
import { Check } from "lucide-react";
import { useSite } from "@/context/SiteContent";

const ease = [0.2, 0.7, 0.2, 1];

const HERO_DOTS = Array.from({ length: 16 });
const HERO_SPARKS = [
  { top: "18%", left: "12%", size: 15, delay: 0 },
  { top: "62%", left: "7%", size: 11, delay: 1.4 },
  { top: "30%", left: "54%", size: 13, delay: 0.7 },
  { top: "74%", left: "46%", size: 10, delay: 2.1 },
];
const fade = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease },
});

const Line = ({ children, delay, className }) => (
  <span className="hero-line-mask">
    <motion.span
      className={className}
      style={{ display: "block" }}
      initial={{ y: "112%", rotate: 2 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.05, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const reduce = useReducedMotion();
  const { hero, price, bookingCta, offer } = useSite();
  const secRef = useRef(null);
  const raf = useRef(null);
  const { scrollY } = useScroll();
  const bookY = useTransform(scrollY, [0, 900], [0, reduce ? 0 : 95]);
  const copyY = useTransform(scrollY, [0, 900], [0, reduce ? 0 : -70]);
  const copyOpacity = useTransform(scrollY, [0, 600], [1, 0.25]);
  const roadY = useTransform(scrollY, [0, 900], [0, reduce ? 0 : 90]);

  const onMove = (e) => {
    const el = secRef.current;
    if (reduce || !el) return;
    const r = el.getBoundingClientRect();
    const px = (((e.clientX - r.left) / r.width) - 0.5) * 18;
    const py = (((e.clientY - r.top) / r.height) - 0.5) * 18;
    if (raf.current) cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      el.style.setProperty("--par-x", `${px.toFixed(1)}px`);
      el.style.setProperty("--par-y", `${py.toFixed(1)}px`);
    });
  };

  return (
    <section ref={secRef} className="hero" data-rail="Start" data-testid="hero-section" onMouseMove={onMove}>
      <div className="hero-layer hero-gridlines" />
      <div className="hero-layer hero-aura" />
      <div className="hero-particles" aria-hidden="true">
        {HERO_DOTS.map((_, i) => (
          <span key={i} className="bs-dot" style={{ left: `${(i * 6.3 + 4) % 96}%`, top: `${(i * 43) % 92}%`, animationDelay: `${(i % 6) * 0.7}s`, animationDuration: `${7 + (i % 5)}s` }} />
        ))}
        {HERO_SPARKS.map((s, i) => (
          <span key={`s${i}`} className="bs-spark" style={{ top: s.top, left: s.left, width: s.size, height: s.size, animationDelay: `${s.delay}s` }} />
        ))}
      </div>
      <motion.div className="hero-road" style={{ y: roadY }}>
        <svg preserveAspectRatio="none" viewBox="0 0 1600 520">
          <defs>
            <linearGradient id="m" x1="0" x2="1"><stop stopColor="#071F1A" /><stop offset="1" stopColor="#12372E" /></linearGradient>
            <linearGradient id="r" x1="0" x2="1"><stop stopColor="#F4EEE8" stopOpacity=".16" /><stop offset=".52" stopColor="#D4AF37" stopOpacity=".75" /><stop offset="1" stopColor="#D4AF37" stopOpacity=".08" /></linearGradient>
          </defs>
          <motion.path d="M0 385C190 230 340 248 515 332C670 212 810 220 985 340C1160 242 1375 247 1600 370L1600 520L0 520Z" fill="url(#m)" initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.4, ease }} />
          <motion.path d="M520 520C690 425 850 370 1040 355C1190 343 1320 360 1470 405C1240 370 1070 386 935 425C810 462 730 493 665 520Z" fill="url(#r)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 0.6 }} />
          <circle cx="1280" cy="235" fill="#D4AF37" opacity=".14" r="88" />
        </svg>
      </motion.div>
      <div className="container hero-grid">
        <motion.div className="hero-copy" style={{ y: copyY, opacity: copyOpacity }}>
          <motion.div className="hero-kicker" {...fade(0.05)} data-testid="hero-kicker"><span className="hero-kicker-dot" />{hero.kicker}</motion.div>
          <div style={{ height: 17 }} />
          <motion.div className="eyebrow" {...fade(0.18)}>{hero.eyebrow}</motion.div>
          <h1 data-testid="hero-title">
            <Line delay={0.28}>{hero.titleLine1}</Line>
            <Line delay={0.4} className="gold">{hero.titleLine2}</Line>
          </h1>
          <motion.p className="lead" {...fade(0.62)}>{hero.lead}</motion.p>
          <motion.p style={{ fontWeight: 800, color: "var(--gold3)" }} {...fade(0.72)}>{hero.byline}</motion.p>
          <motion.div className="hero-actions" {...fade(0.82)}>
            <Magnetic><CheckoutButton testId="hero-checkout-btn">{hero.primaryCta} • {price}</CheckoutButton></Magnetic>
            <Magnetic><a className="btn btn-outline" href="#booking" data-testid="hero-booking-btn">{bookingCta}</a></Magnetic>
          </motion.div>
          {offer.includeBadge && (
            <motion.div className="hero-include-badge" {...fade(0.86)} data-testid="hero-include-badge">
              <Check size={14} strokeWidth={3} /> {offer.includeBadge}
            </motion.div>
          )}
          <motion.a className="hero-quiz-textlink" href="#quiz" {...fade(0.88)} data-testid="hero-quiz-btn">{hero.secondaryCta} →</motion.a>
          <motion.div className="hero-trust" {...fade(0.92)}>
            <span>One-time purchase</span><span>Instant download after payment</span><span>No subscription</span>
          </motion.div>
          <motion.p className="hero-microcopy" {...fade(0.98)} data-testid="hero-microcopy">{hero.microcopy}</motion.p>
          <div className="hero-benefits">
            {[["01", "More clarity"], ["02", "More confidence"], ["03", "Better decisions"], ["04", "Brighter tomorrows"]].map(([n, t], i) => (
              <motion.div key={n} className="hero-benefit" {...fade(1.04 + i * 0.08)}><b>{n}</b><small>{t}</small></motion.div>
            ))}
          </div>
        </motion.div>
        <motion.div className="hero-book-parallax" style={{ y: bookY }}>
          <Book3D />
        </motion.div>
      </div>
    </section>
  );
};
